import type { ReactNode } from "react";
import { Reveal } from "../Reveal";
import type { MarketingCopy } from "../copy";
import { BookIcon, CameraIcon, ShieldIcon, TargetIcon, VoiceIcon } from "../icons";

export interface TrustStripProps {
  t: MarketingCopy;
}

/**
 * Five named, checkable facts, overlapping the bottom of the hero.
 *
 * 🔴 EACH ONE NAMES WHAT IT ASSERTS — the standard by its number, the hardware,
 * the camera behaviour — so a school can check it. "Safe", "aligned" and
 * "fun" are categories, not claims; this strip carries none of them.
 *
 * ⚠️ THE ICON TINTS ARE THE SKILL/BRAND TOKENS AT 12%, and the icons themselves
 * the full token. No hex is typed here.
 */
const ICONS: { icon: ReactNode; tint: string }[] = [
  { icon: <TargetIcon className="h-5 w-5 text-primary" />, tint: "bg-primary-tint" },
  { icon: <BookIcon className="h-5 w-5 text-sky" />, tint: "bg-sky/12" },
  { icon: <VoiceIcon className="h-5 w-5 text-warning" />, tint: "bg-sunshine/18" },
  { icon: <CameraIcon className="h-5 w-5 text-kid-secondary-fg" />, tint: "bg-leaf/14" },
  { icon: <ShieldIcon className="h-5 w-5 text-teal" />, tint: "bg-teal/12" },
];

export function TrustStrip({ t }: TrustStripProps) {
  return (
    <div className="relative z-20 mx-auto -mt-6 w-full max-w-[1240px] px-5 sm:-mt-10 sm:px-6 lg:-mt-14 lg:px-10">
      <Reveal>
        <ul className="grid grid-cols-1 gap-1 rounded-2xl border border-line bg-surface p-2 shadow-[0_24px_60px_-24px_color-mix(in_srgb,var(--color-ink)_28%,transparent)] min-[480px]:grid-cols-2 lg:grid-cols-5">
          {t.trust.map((fact, i) => (
            <li
              key={fact.title}
              className="flex items-center gap-3.5 rounded-xl px-4 py-3.5 lg:border-l lg:border-line lg:first:border-l-0"
            >
              <span aria-hidden="true" className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${ICONS[i]?.tint ?? ""}`}>
                {ICONS[i]?.icon}
              </span>
              <span>
                <span className="block font-dash text-dash-sm leading-snug font-extrabold text-ink">{fact.title}</span>
                <span className="block font-dash text-dash-xs font-semibold text-muted">{fact.body}</span>
              </span>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  );
}
